import {
  formatCurrency,
  formatMonthsOrYears,
  enumLabel,
  isSentinel,
  sentinelLabel,
  rateGuaranteeHasNoCap,
  ANNUAL_OPEN_ENROLLMENT_LABELS,
  EMPLOYER_CONTRIBUTION_LABELS,
} from "./shared";

export type DentalHmoFieldFormatter = (row: Record<string, unknown>) => string;

/** Rate Guarantee / Rate Caps — siblings: rateGuaranteeAmount, rateGuaranteeCap. */
const formatRateGuarantee: DentalHmoFieldFormatter = (row) => {
  const type = row["rateGuaranteeType"];
  const cap = row["rateGuaranteeCap"];

  const capText =
    typeof cap === "string" && !rateGuaranteeHasNoCap(type) ? cap.trim() : "";

  if (isSentinel(type)) {
    const label = sentinelLabel(type as string);
    return capText ? `${label}\n${capText}` : label;
  }

  // Deliberately unlike the shared rate guarantee formatter: dental leaves a
  // months/years type with no amount blank instead of printing the bare type label.
  if (row["rateGuaranteeAmount"] == null) return "";
  const line = formatMonthsOrYears(type, row["rateGuaranteeAmount"]);
  if (!line) return "";
  return capText ? `${line}\n${capText}` : line;
};

const adaCost =
  (fieldKey: string): DentalHmoFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    if (value == null || value === "") return "";
    return formatCurrency(value);
  };

const ADA_CODE_KEYS = [
  "adaCodeD0120",
  "adaCodeD0150",
  "adaCodeD1110",
  "adaCodeD1120",
  "adaCodeD1206",
  "adaCodeD1351",
  "adaCodeD2140",
  "adaCodeD2150",
  "adaCodeD2160",
  "adaCodeD2161",
  "adaCodeD2751",
  "adaCodeD2752",
  "adaCodeD3310",
  "adaCodeD3320",
  "adaCodeD3330",
  "adaCodeD3410",
  "adaCodeD4210",
  "adaCodeD4341",
  "adaCodeD4910",
  "adaCodeD5110",
  "adaCodeD5120",
  "adaCodeD5213",
  "adaCodeD7111",
  "adaCodeD7140",
  "adaCodeD7210",
  "adaCodeD8080",
  "adaCodeD8090",
] as const;

export const dentalHmoFormatters: Record<string, DentalHmoFieldFormatter> = {
  ...Object.fromEntries(ADA_CODE_KEYS.map((key) => [key, adaCost(key)])),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  employerContribution: enumLabel(
    "employerContribution",
    EMPLOYER_CONTRIBUTION_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,
};

export const formatDentalHmoField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = dentalHmoFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
